import { colorTokens, colorHelpersFor, colors as colors$1, shadowInvisible, shadows as shadows$1, shadowHalos, shadowHalo, shadowRings, shadowRing, motion } from '@hover/blueprint-theme';
import { fromEntries, capitalize, isNumber, entries } from '@hover/utilities';
import 'ramda/src/values';
import _mapObjIndexed from 'ramda/src/mapObjIndexed';
import 'ramda/src/map';
import 'ramda/src/mergeDeepWith';
import { cssVar } from '@chakra-ui/theme-tools';
import _max from 'ramda/src/max';
import _reduce from 'ramda/src/reduce';
import { theme } from '@chakra-ui/react';
import { transitions } from 'polished';

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

var cssVarPrefix = 'hover';

var configuration = /*#__PURE__*/Object.freeze({
  __proto__: null,
  cssVarPrefix: cssVarPrefix
});

/**
 * Create a CSS variable with the theme prefix. This is mostly useful for
 * referencing the variables that Chakra generates for us from our theme
 * foundation (tokens)
 *
 * @see {@link https://chakra-ui.com/docs/styled-system/css-variables}
 *
 * @param name name of the CSS variable (e.g: `foo` → `--hover-foo`)
 */
var cssVarPrefixed = function cssVarPrefixed(name) {
  return cssVar("".concat(cssVarPrefix, "-").concat(name));
};

/**
 * Helper functions for creating CSS variables to reference the variables
 * that Chakra generates for us from our theme foundation (tokens).
 *
 * @example
 * ```ts
 * variables.size(100).reference // `var(--hover-sizes-100)`
 * variables.size(100).variable // `--hover-sizes-100`
 *
 * @see {@link https://chakra-ui.com/docs/styled-system/css-variables}
 */
var variables = {
  color: function color(_color) {
    return cssVarPrefixed("colors-".concat(_color.split('.').join('-')));
  },
  size: function size(_size) {
    return cssVarPrefixed("sizes-".concat(_size));
  },
  space: function space(_space) {
    return cssVarPrefixed("space-".concat(_space));
  },
  radius: function radius(_radius) {
    return cssVarPrefixed("radii-".concat(_radius));
  }
};

// NOTE: we need to implement `ColorHues` for each palette in
// order for Chakra to consider a palette as a `colorScheme`
// For backward compatibility with legacy Blueprint colors
// (e.g: `primary500` instead of Chakra-style `primary.500`)
var legacyColors = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, colorTokens), {}, {
  neutral000: colorTokens.neutral0
});
var defineColors = function defineColors(colors) {
  var palettes = {
    primary: colors.primary,
    secondary: _objectSpread2({
      50: colors.secondary[100]
    }, colors.secondary),
    success: colors.success,
    warning: colors.warning,
    danger: colors.danger,
    neutral: colors.neutral,
    brandNavy: _objectSpread2({
      50: colors.brandNavy[100]
    }, colors.brandNavy),
    brandGreen: _objectSpread2({
      50: colors.brandGreen[100]
    }, colors.brandGreen),
    brandBrown: _objectSpread2({
      50: colors.brandBrown[100]
    }, colors.brandBrown),
    brandRed: _objectSpread2({
      50: colors.brandRed[100]
    }, colors.brandRed),
    brandTan: _objectSpread2({
      50: colors.brandTan[100]
    }, colors.brandTan),
    brandOrange: _objectSpread2({
      50: colors.brandOrange[100]
    }, colors.brandOrange),
    brandYellow: _objectSpread2({
      50: colors.brandYellow[100]
    }, colors.brandYellow),
    brandPurple: _objectSpread2({
      50: colors.brandPurple[100]
    }, colors.brandPurple),
    darkAlpha: colors.darkAlpha,
    lightAlpha: colors.lightAlpha
  };
  var allColors = _objectSpread2(_objectSpread2({}, palettes), {}, {
    blue: palettes.primary,
    green: palettes.brandGreen,
    orange: palettes.brandOrange,
    purple: palettes.brandPurple,
    red: palettes.brandRed,
    yellow: palettes.brandYellow,
    halo: colors.halo,
    ring: colors.ring,
    border: colors.border
  }, legacyColors);
  return _objectSpread2(_objectSpread2({}, allColors), colorHelpersFor(colors));
};
var colors = /*#__PURE__*/defineColors(colors$1);

var shadows = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, shadows$1), /*#__PURE__*/fromEntries( /*#__PURE__*/shadowHalos.map(function (halo) {
  return ["halo".concat(capitalize(halo)), shadowHalo(variables.color("halo.".concat(halo)).reference)];
}))), /*#__PURE__*/fromEntries( /*#__PURE__*/shadowRings.map(function (ring) {
  return ["ring".concat(capitalize(ring)), shadowRing(variables.color("ring.".concat(ring)).reference)];
}))), {}, {
  invisibleNeutral: /*#__PURE__*/shadowInvisible( /*#__PURE__*/variables.color('halo.neutral').reference)
});

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = null == arr ? null : "undefined" != typeof Symbol && arr[Symbol.iterator] || arr["@@iterator"];
  if (null != _i) {
    var _s,
      _e,
      _x,
      _r,
      _arr = [],
      _n = !0,
      _d = !1;
    try {
      if (_x = (_i = _i.call(arr)).next, 0 === i) {
        if (Object(_i) !== _i) return;
        _n = !1;
      } else for (; !(_n = (_s = _x.call(_i)).done) && (_arr.push(_s.value), _arr.length !== i); _n = !0);
    } catch (err) {
      _d = !0, _e = err;
    } finally {
      try {
        if (!_n && null != _i.return && (_r = _i.return(), Object(_r) !== _r)) return;
      } finally {
        if (_d) throw _e;
      }
    }
    return _arr;
  }
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

// Get the highest z-index from Chakra
var highest = /*#__PURE__*/_reduce(_max, 0, /*#__PURE__*/Object.values(theme.zIndices).filter(isNumber));
var zIndices = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, theme.zIndices), {}, {
  loading: highest + 100
});

/* eslint-disable no-nested-ternary */
var focusSelector = ':focus';
var focusVisibleSelector = ':focus-visible';
var focusVisibleInvertSelector = ':focus:not(:focus-visible)';
var invert = function invert(styles) {
  return _mapObjIndexed(function () {
    return 'none';
  }, styles);
};

/**
 * Override browser default focus styles with provided styles.
 *
 * Currently supports `:focus-visible` via the second parameter. Because not
 * all browsers (looking at you Safari) support `:focus-visible`, we need to
 * first apply any provides styles to `:focus` and then remove them in browsers
 * that support `:focus-visible`. Passing a `true` to `visibleOnly` will simply
 * try to "invert" the provided focus styles by setting each property to
 * `none`. For finer grained control, pass a `CSSObject` as the `visibleOnly`
 * parameter to provide the default unfocused state that will be applied to
 * browsers that do support `:focus-visible`.
 *
 * @example
 * Apply custom styles to `:focus`
 * ```ts
 * resetFocus({ border: '1px solid red' })
 * ```
 *
 * @example
 * Apply custom styles to `:focus-visible` (this will only work if the default state
 * does not include a `border`, see next example for how to handle that case)
 * ```ts
 * resetFocus({ border: '1px solid red' }, true);
 * ```
 *
 * @example
 * Apply custom styles to `:focus-visible` with unfocused defaults
 * ```ts
 * resetFocus({ border: '1px solid red' }, { border: '1px solid gray' }});
 * ```
 *
 * @param styles styles to apply for `:focus` state
 * @param visibleOnly whether to apply styles only to the `:focus-visible` state (see above)
 * @returns style object with custom focus styels and browser focus reset
 */
var resetFocus = function resetFocus() {
  var _ref;
  var styles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var visibleOnly = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return _ref = {}, _defineProperty(_ref, focusSelector, _objectSpread2({
    outline: 0
  }, styles)), _defineProperty(_ref, focusVisibleSelector, _objectSpread2({
    outline: 0
  }, styles)), _defineProperty(_ref, focusVisibleInvertSelector, _objectSpread2({}, visibleOnly ? visibleOnly === true ? invert(styles) : visibleOnly : {})), _defineProperty(_ref, '::-moz-focus-inner', {
    border: 0,
    outline: 0
  }), _ref;
};
var noFocus = /*#__PURE__*/resetFocus();

/**
 * CSS transition preset helpers (applies system easing
 * tokens to polished's `transitions` helper)
 *
 * @example
 * ```tsx
 * import { transition, system } from '@hover/blueprint';
 *
 * <system.div {...transition.easing250('height', 'opacity')} />
 * ```
 */
var transition = /*#__PURE__*/fromEntries( /*#__PURE__*/entries(motion.easings).map(function (_ref) {
  var _ref2 = _slicedToArray(_ref, 2),
    name = _ref2[0],
    easing = _ref2[1];
  var t = function t() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return transitions(args, easing);
  };
  return ["easing".concat(name), t];
}));

export { _objectSpread2 as _, _defineProperty as a, _slicedToArray as b, _objectWithoutProperties as c, defineColors as d, configuration as e, cssVarPrefixed as f, _arrayLikeToArray as g, _unsupportedIterableToArray as h, colors as i, legacyColors as l, noFocus as n, resetFocus as r, shadows as s, transition as t, variables as v, zIndices as z };
